import { useState, useEffect, useRef } from "react";
import { usePersistentStorage } from "../hooks/useGameHooks";
import {
  CHARACTERS_MAP,
  MUSIC_MAP,
  CRAFTS_MAP,
  QUOTES_MAP,
  TRIVIA_MAP,
  LOCATIONS_MAP,
  SILHOUETTES_MAP,
} from "../maps";
import { CHARACTERS, QUOTES, MUSIC, LOCATIONS, TRIVIA, CRAFTS, SILHOUETTES } from "../data";
import { getDailyItem } from "../utils/gameLogic";
import { recalculateAllStats } from "../utils/recalculateStats";
import {
  FACTIONS,
  FACTION_NAMES,
  getCurrentFactionMonth,
  getFactionLeaderboard,
  getFactionHistory,
  getFactionRewardBadge,
} from "../utils/factionLogic";
import { GAME_ORDER } from "./CharacterMode";
import CollapsibleSection from "./CollapsibleSection";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Card displayed in the locations collection tab.
 * Shows an image carousel for unlocked locations, or a nation-themed placeholder when locked.
 *
 * @param {{ loc: Object, isUnlocked: boolean, onSelect: (loc: Object) => void }} props
 * @returns {JSX.Element}
 */
function LocationCard({ loc, isUnlocked, onSelect }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  /** Returns a nation-themed placeholder image path for locked location cards. */
  const getNationImage = (nation) => {
    const n = (nation || "").toLowerCase();
    if (n.includes("liberl")) return "/images/nations/liberl.webp";
    if (n.includes("erebonia")) return "/images/nations/erebonia.webp";
    if (n.includes("crossbell")) return "/images/nations/crossbell.webp";
    if (n.includes("calvard")) return "/images/nations/calvard.webp";
    return "/images/nations/zemuria.webp";
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % 5);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + 5) % 5);
  };

  return (
    <div className={`collection-item-card ${isUnlocked ? "unlocked" : "locked"}`} onClick={() => isUnlocked && onSelect(loc)}>
      <div className="location-card-image-wrapper">
        <img
          src={isUnlocked ? loc.imageFilePaths[currentImageIndex] : getNationImage(loc.locationNation)}
          alt={loc.locationName}
          className="collection-item-image"
          loading="lazy"
          onError={(e) => { e.target.src = "/images/nations/zemuria.webp"; }}
        />
        {isUnlocked && (
          <>
            <button className="location-flip-btn prev" onClick={handlePrev} title="Previous Image">◀</button>
            <button className="location-flip-btn next" onClick={handleNext} title="Next Image">▶</button>
            <div className="location-image-counter">{currentImageIndex + 1}/5</div>
          </>
        )}
      </div>
      <h4 className="collection-item-title">{isUnlocked ? loc.locationName : "???"}</h4>
      <p className="collection-item-subtitle">{isUnlocked ? loc.locationNation : "Unknown Region"}</p>
    </div>
  );
}

/**
 * Collection browser showing all 7 modes' unlockable items grouped by game (or nation for locations).
 * Supports collapsible sections, progress bars, inline music preview, a detail modal with usage stats,
 * and a trivia keyword search. Unlocked items are earned by winning daily puzzles.
 *
 * @param {{ version: string, currentTheme: string, collectionExpandDefault: boolean }} props
 * @param {string} props.version - "old" or "new" — controls which mode tabs are shown.
 * @param {string} props.currentTheme - Active era theme name.
 * @param {boolean} props.collectionExpandDefault - Whether collection sections start expanded.
 * @returns {JSX.Element} The collection page.
 */
export default function CollectionMode({ version, currentTheme, collectionExpandDefault }) {
  const [activeSubTab, setActiveSubTab] = useState("characters");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);

  const [playingTrackId, setPlayingTrackId] = useState(null);
  const [collectionVolume, setCollectionVolume] = usePersistentStorage("kisekidle-collection-volume", 0.5);
  const audioRef = useRef(null);

  const [factionMonthsData, setFactionMonthsData] = useState(null);
  const [factionHistory] = useState(() => {
    if (process.env.NODE_ENV === "development") {
      const existing = getFactionHistory();
      if (Object.keys(existing).length === 0) {
        const mock = { "2026-04": "ouroboros", "2026-05": "bracer" };
        localStorage.setItem("kisekidle-faction-history", JSON.stringify(mock));
        if (!localStorage.getItem("kisekidle-faction")) {
          localStorage.setItem("kisekidle-faction", "bracer");
          localStorage.setItem("kisekidle-faction-points", "37");
        }
        return mock;
      }
      return existing;
    }
    return getFactionHistory();
  });
  const [factionLoading, setFactionLoading] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = collectionVolume;
    }
  }, [collectionVolume]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Fetch faction month data when tab is active
  useEffect(() => {
    if (activeSubTab !== "factions") return;
    if (factionMonthsData) return;

    const DEV_MOCK = process.env.NODE_ENV === "development" ? [
      { month: "2026-04", data: { bracer: 47, ouroboros: 62, church: 31 } },
      { month: "2026-05", data: { bracer: 88, ouroboros: 55, church: 73 } },
    ] : null;

    setFactionLoading(true);
    const current = getCurrentFactionMonth();
    const months = [];
    const epoch = new Date("2026-04-01T00:00:00Z");
    const now = new Date();
    const d = new Date(epoch);
    while (d <= now) {
      const m = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      if (m === current) break;
      months.push(m);
      d.setUTCMonth(d.getUTCMonth() + 1);
    }

    Promise.all(months.map(m => getFactionLeaderboard(m).then(data => ({ month: m, data }))))
      .then((results) => {
        let filtered = results.filter(r => r.data.bracer > 0 || r.data.ouroboros > 0 || r.data.church > 0);
        if (filtered.length === 0 && DEV_MOCK) filtered = DEV_MOCK;
        setFactionMonthsData(filtered);
        setFactionLoading(false);
      })
      .catch(() => {
        if (DEV_MOCK) setFactionMonthsData(DEV_MOCK);
        setFactionLoading(false);
      });
  }, [activeSubTab, factionMonthsData]);

  /** Plays or pauses a music track preview in the collection browser. */
  const togglePlay = (track) => {
    if (playingTrackId === track.id) {
      audioRef.current.pause();
      setPlayingTrackId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(track.audioUrl);
      audioRef.current.volume = collectionVolume;
      audioRef.current.play().catch(err => console.error("Audio playback failed:", err));
      setPlayingTrackId(track.id);
      audioRef.current.onended = () => setPlayingTrackId(null);
    }
  };

  // Collections for each mode
  const [charCol] = usePersistentStorage("kisekidle-collection", []);
  const [quoteCol] = usePersistentStorage("kisekidle-collection-quotes", []);
  const [musicCol] = usePersistentStorage("kisekidle-collection-music", []);
  const [locCol] = usePersistentStorage("kisekidle-collection-locations", []);
  const [triviaCol] = usePersistentStorage("kisekidle-collection-trivia", []);
  const [craftCol] = usePersistentStorage("kisekidle-collection-crafts", []);
  const [silCol] = usePersistentStorage("kisekidle-collection-silhouette", []);
  const [locationViewMode, setLocationViewMode] = useState("list"); // "list" or "map"
  const [activeClusterId, setActiveClusterId] = useState(null); // Track which pin popup is open

  const unlockedCharIds = new Set(charCol.map(id => String(id)));
  const unlockedQuoteIds = new Set(quoteCol.map(id => String(id)));
  const unlockedMusicIds = new Set(musicCol.map(id => String(id)));
  const unlockedLocIds = new Set(locCol.map(id => String(id)));
  const unlockedTriviaIds = new Set(triviaCol.map(id => String(id)));
  const unlockedCraftIds = new Set(craftCol.map(id => String(id)));
  const unlockedSilIds = new Set(silCol.map(id => String(id)));

  /** Formats a trait value for display — joins arrays, replaces null/undefined with "N/A". */
  const formatProfileTrait = (value) => {
    if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "N/A";
    if (value === null || value === undefined || value === "") return "N/A";
    return value;
  };

  /** Scans all localStorage game records to compute how many times an item was guessed and won. */
  const getItemUsageStats = (itemId, mode) => {
    const normalizedId = String(itemId);
    let timesGuessed = 0;
    let timesCorrect = 0;

    const config = {
      characters: { key: "kisekidle-chars", data: CHARACTERS, map: CHARACTERS_MAP },
      music: { key: "kisekidle-music", data: MUSIC, map: MUSIC_MAP },
      quotes: { key: "kisekidle-quotes", data: QUOTES, map: QUOTES_MAP },
      locations: { key: "kisekidle-locations", data: LOCATIONS, map: LOCATIONS_MAP },
      trivia: { key: "kisekidle-trivia", data: TRIVIA, map: TRIVIA_MAP },
      crafts: { key: "kisekidle-crafts", data: CRAFTS, map: CRAFTS_MAP },
      silhouette: { key: "kisekidle-silhouette", data: SILHOUETTES, map: SILHOUETTES_MAP }
    }[mode];

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || (key !== config.key && !key.startsWith(`${config.key}-`))) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || "{}");
        const guesses = Array.isArray(parsed.guesses) ? parsed.guesses : [];

        const dateStr = parsed.date || (key.startsWith(`${config.key}-`) ? key.replace(`${config.key}-`, "") : null);
        const parts = dateStr?.split("-").map(Number);
        if (parts?.length === 3) {
          const dateObj = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
          const target = getDailyItem(config.data, dateObj, config.map);

          if (mode === 'silhouette') {
            if (String(target?.id) === normalizedId) {
              timesGuessed += 1;
              if (guesses.includes(target?.character)) timesCorrect += 1;
            }
          } else {
            const isGuessed = guesses.some(g => {
              if (mode === 'quotes') return String(CHARACTERS.find(c => c.name === g?.name)?.id) === normalizedId;
              return String(g?.id) === normalizedId;
            });

            if (isGuessed) {
              timesGuessed += 1;
              let isWin = false;
              if (mode === 'quotes') isWin = String(CHARACTERS.find(c => c.name === target?.character)?.id) === normalizedId;
              else isWin = String(target?.id) === normalizedId;
              if (isWin) timesCorrect += 1;
            }
          }
        }
      } catch (e) { }
    }
    return { timesGuessed, timesCorrect, successRate: timesGuessed > 0 ? Math.round((timesCorrect / timesGuessed) * 100) : 0 };
  };

  const [expandedSections, setExpandedSections] = useState(() => {
    if (!collectionExpandDefault) return {};
    const allExpanded = {};
    GAME_ORDER.forEach(game => {
      allExpanded[`char-${game}`] = true;
      allExpanded[`music-${game}`] = true;
      allExpanded[`quote-${game}`] = true;
      allExpanded[`craft-${game}`] = true;
      allExpanded[`sil-${game}`] = true;
    });
    LOCATIONS.forEach(l => {
      allExpanded[`loc-${l.locationNation}`] = true;
    });
    return allExpanded;
  });
  const [triviaSearch, setTriviaSearch] = useState("");

  /** Toggles the expanded state of a collapsible section. */
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  /** Normalizes a game name string into one of the canonical GAME_ORDER keys. */
  const getGameGroupKey = (gameStr) => {
    if (!gameStr) return "Unknown";
    if (GAME_ORDER.includes(gameStr)) return gameStr;

    const upper = gameStr.toUpperCase();
    if (upper.includes("FC") || upper.includes("FIRST CHAPTER")) return "FC";
    if (upper.includes("SC") || upper.includes("SECOND CHAPTER")) return "SC";
    if (upper.includes("3RD") || upper.includes("THIRD")) return "3rd";
    if (upper.includes("ZERO")) return "Zero";
    if (upper.includes("AZURE") || upper.includes("AO")) return "Azure";
    if (upper.includes("COLD STEEL 1") || upper.includes("CS1")) return "CS1";
    if (upper.includes("COLD STEEL 2") || upper.includes("CS2")) return "CS2";
    if (upper.includes("COLD STEEL 3") || upper.includes("CS3")) return "CS3";
    if (upper.includes("COLD STEEL 4") || upper.includes("CS4")) return "CS4";
    if (upper.includes("REVERIE") || upper.includes("HAJIMARI")) return "Reverie";
    if (upper.includes("DAYBREAK 2") || upper.includes("KURO 2")) return "Daybreak 2";
    if (upper.includes("DAYBREAK") || upper.includes("KURO")) return "Daybreak";
    if (upper.includes("TRAILS IN THE SKY")) return "FC";

    return gameStr;
  };

  const tabs = [
    { id: "characters", label: "🎭 Characters", icon: "🎭" },
    { id: "music", label: "🎵 Music", icon: "🎵" },
    { id: "quotes", label: "💬 Quotes", icon: "💬" },
    { id: "locations", label: "📍 Locations", icon: "📍" },
    { id: "trivia", label: "🧠 Trivia", icon: "🧠" },
    { id: "crafts", label: "⚔️ Crafts", icon: "⚔️" },
    { id: "silhouette", label: "👥 Silhouettes", icon: "👥" },
    { id: "factions", label: "🏆 Factions", icon: "🏆" },
  ];

  const filteredTabs = version === "new" ? tabs : tabs.slice(0, 4);

  const grandTotal = CHARACTERS.length + MUSIC.length + QUOTES.length + LOCATIONS.length + TRIVIA.length + CRAFTS.length + SILHOUETTES.length;
  const grandUnlocked = unlockedCharIds.size + unlockedMusicIds.size + unlockedQuoteIds.size + unlockedLocIds.size + unlockedTriviaIds.size + unlockedCraftIds.size + unlockedSilIds.size;
  const grandPct = grandTotal > 0 ? Math.round((grandUnlocked / grandTotal) * 100) : 0;

  const tabTotals = {
    characters: { total: CHARACTERS.length, unlocked: unlockedCharIds.size },
    music: { total: MUSIC.length, unlocked: unlockedMusicIds.size },
    quotes: { total: QUOTES.length, unlocked: unlockedQuoteIds.size },
    locations: { total: LOCATIONS.length, unlocked: unlockedLocIds.size },
    trivia: { total: TRIVIA.length, unlocked: unlockedTriviaIds.size },
    crafts: { total: CRAFTS.length, unlocked: unlockedCraftIds.size },
    silhouette: { total: SILHOUETTES.length, unlocked: unlockedSilIds.size },
  };

  /** Horizontal progress bar showing unlocked/total with percentage width. */
  const ProgressBar = ({ unlocked, total }) => {
    const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;
    return (
      <div className="collection-progress-track">
        <div className="collection-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  return (
    <div className={`profile-container theme-${currentTheme}`}>
      <div className="header-container collection-header">
        <div>
          <h2 className="mode-title">📘 Central Collection</h2>
          <p style={{ color: "#a0a5b5" }}>Archives of your discovered Zemurian secrets</p>
        </div>
        <div className="collection-header-controls">
          <div className="collection-volume-control">
            <span className="volume-icon">{collectionVolume === 0 ? "🔇" : collectionVolume < 0.5 ? "🔉" : "🔊"}</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={collectionVolume}
              onChange={(e) => setCollectionVolume(parseFloat(e.target.value))}
              className="volume-slider"
            />
            <span className="volume-percent">{Math.round(collectionVolume * 100)}%</span>
          </div>
          <button
            className="recalculate-stats-btn"
            onClick={() => {
              if (window.confirm("This will scan your entire play history to rebuild your collection and profile data. The page will reload. Proceed?")) {
                recalculateAllStats();
                window.location.reload();
              }
            }}
            title="Recalculate all stats from your local play history"
          >
            🔄 Recalculate
          </button>
        </div>
      </div>

      {/* Grand Total Progress */}
      <div className="collection-progress-container">
        <div className="collection-progress-header">
          <span>Total Collection</span>
          <span>{grandUnlocked} / {grandTotal} ({grandPct}%)</span>
        </div>
        <ProgressBar unlocked={grandUnlocked} total={grandTotal} />
      </div>

      <div className="collection-tabs">
        {filteredTabs.map(tab => (
          <button
            key={tab.id}
            className={`collection-tab-btn ${activeSubTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === "characters" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Characters</span>
              <span>{tabTotals.characters.unlocked} / {tabTotals.characters.total} ({tabTotals.characters.total > 0 ? Math.round((tabTotals.characters.unlocked / tabTotals.characters.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.characters.unlocked} total={tabTotals.characters.total} />
          </div>
          {GAME_ORDER.map(gameName => {
            const chars = CHARACTERS.filter(c => c.debutGame === gameName);
            if (chars.length === 0) return null;
            const unlocked = chars.filter(c => unlockedCharIds.has(String(c.id))).length;
            const id = `char-${gameName}`;
            return (
              <CollapsibleSection
                key={gameName}
                id={id}
                title={gameName}
                count={unlocked}
                total={chars.length}
                isExpanded={expandedSections[id]}
                onToggle={toggleSection}
              >
                <motion.div 
                  className="collection-grid"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.05 } }
                  }}
                >
                  {chars.map((char, idx) => {
                    const isUnlocked = unlockedCharIds.has(String(char.id));
                    return (
                      <motion.div
                        key={char.id}
                        variants={{
                          hidden: { opacity: 0, scale: 0.8 },
                          visible: { opacity: 1, scale: 1 }
                        }}
                        className={`char-portrait-box premium-hover ${isUnlocked ? "unlocked" : ""}`}
                        onClick={() => isUnlocked && (setSelectedItem(char), setSelectedMode("characters"))}
                        style={{ cursor: isUnlocked ? "pointer" : "default" }}
                      >
                        {isUnlocked ? (
                          <img src={char.imageUrl} alt={char.name} className="char-image" loading="lazy" onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = '<span style="font-size:10px;text-align:center;padding:5px;">Image Missing</span>'; }} />
                        ) : <div className="locked-question-mark">?</div>}
                        {isUnlocked && <div className="char-name-label">{char.name}</div>}
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      {activeSubTab === "music" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Music</span>
              <span>{tabTotals.music.unlocked} / {tabTotals.music.total} ({tabTotals.music.total > 0 ? Math.round((tabTotals.music.unlocked / tabTotals.music.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.music.unlocked} total={tabTotals.music.total} />
          </div>
          {GAME_ORDER.map(gameName => {
            const tracks = MUSIC.filter(m => getGameGroupKey(m.game) === gameName);
            if (tracks.length === 0) return null;
            const unlocked = tracks.filter(m => unlockedMusicIds.has(String(m.id))).length;
            const id = `music-${gameName}`;
            return (
              <CollapsibleSection
                key={gameName}
                id={id}
                title={gameName}
                count={unlocked}
                total={tracks.length}
                isExpanded={expandedSections[id]}
                onToggle={toggleSection}
              >
                <motion.div 
                  className="collection-list"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.05 } }
                  }}
                >
                  {tracks.map(track => {
                    const isUnlocked = unlockedMusicIds.has(String(track.id));
                    return (
                      <motion.div
                        key={track.id}
                        variants={{
                          hidden: { opacity: 0, x: -20 },
                          visible: { opacity: 1, x: 0 }
                        }}
                        className={`collection-list-item premium-hover ${isUnlocked ? "unlocked" : "locked"}`}
                        onClick={() => isUnlocked && (setSelectedItem(track), setSelectedMode("music"))}
                      >
                        <div className="collection-icon-circle">
                          {isUnlocked && track.albumArt ? (
                            <img src={track.albumArt} alt="Art" loading="lazy" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                          ) : "🎵"}
                        </div>
                        <div className="collection-list-content">
                          <h4 className="collection-item-title">{isUnlocked ? track.title : "???"}</h4>
                          <p className="collection-item-subtitle">{isUnlocked ? `${track.game} (${track.type})` : "Undiscovered Track"}</p>
                        </div>
                        {isUnlocked && (
                          <button
                            className="collection-play-btn"
                            onClick={(e) => { e.stopPropagation(); togglePlay(track); }}
                            title={playingTrackId === track.id ? "Pause" : "Play"}
                          >
                            {playingTrackId === track.id ? "⏸️" : "▶️"}
                          </button>
                        )}
                        {isUnlocked && <span className="collection-badge music">UNLOCKED</span>}
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      {activeSubTab === "quotes" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Quotes</span>
              <span>{tabTotals.quotes.unlocked} / {tabTotals.quotes.total} ({tabTotals.quotes.total > 0 ? Math.round((tabTotals.quotes.unlocked / tabTotals.quotes.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.quotes.unlocked} total={tabTotals.quotes.total} />
          </div>
          {GAME_ORDER.map(gameName => {
            const gameQuotes = QUOTES.filter(q => getGameGroupKey(q.game) === gameName);
            if (gameQuotes.length === 0) return null;
            const unlocked = gameQuotes.filter(q => unlockedQuoteIds.has(String(q.id))).length;
            const id = `quote-${gameName}`;
            return (
              <CollapsibleSection
                key={gameName}
                id={id}
                title={gameName}
                count={unlocked}
                total={gameQuotes.length}
                isExpanded={expandedSections[id]}
                onToggle={toggleSection}
              >
                <motion.div 
                  className="collection-list"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.03 } }
                  }}
                >
                  {gameQuotes.map((q, idx) => {
                    const speaker = CHARACTERS.find(c => c.name === q.character);
                    const isUnlocked = unlockedQuoteIds.has(String(q.id));
                    const recipientChar = CHARACTERS.find(c => c.name === q.recipient);
                    const recipientImg = q.recipientImage || recipientChar?.imageUrl;

                    return (
                      <motion.div
                        key={idx}
                        variants={{
                          hidden: { opacity: 0, x: -15 },
                          visible: { opacity: 1, x: 0 }
                        }}
                        className={`collection-list-item quote-item-horizontal ${isUnlocked ? "unlocked" : "locked"}`}
                        onClick={() => {
                          if (isUnlocked) {
                            const speaker = CHARACTERS.find(c => c.name === q.character);
                            setSelectedItem({ ...q, id: speaker?.id || q.character });
                            setSelectedMode("quotes");
                          }
                        }}
                      >
                        <div className="quote-side-profile">
                          {isUnlocked && speaker?.imageUrl ? (
                            <img src={speaker.imageUrl} className="quote-mini-img" loading="lazy" alt={q.character} />
                          ) : <div className="quote-mini-img placeholder">👤</div>}
                          <span className="quote-side-name">{isUnlocked ? q.character : "???"}</span>
                        </div>

                        <div className="collection-list-content quote-main-content">
                          <p className="quote-text-mini">
                            "{isUnlocked ? q.quote : "..................................."}"
                          </p>
                          <div className="quote-game-tag">{isUnlocked ? q.game : "Unknown Game"}</div>
                        </div>

                        <div className="quote-side-profile recipient">
                          <span className="quote-side-name">{isUnlocked ? (q.recipient || "General") : "???"}</span>
                          {isUnlocked && recipientImg ? (
                            <img src={recipientImg} className="quote-mini-img" loading="lazy" alt={q.recipient} />
                          ) : <div className="quote-mini-img placeholder">{isUnlocked && q.recipient ? "👥" : "📢"}</div>}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      {activeSubTab === "locations" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Locations</span>
              <span>{tabTotals.locations.unlocked} / {tabTotals.locations.total} ({tabTotals.locations.total > 0 ? Math.round((tabTotals.locations.unlocked / tabTotals.locations.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.locations.unlocked} total={tabTotals.locations.total} />
            
            <div className="locations-view-toggle">
              <button 
                className={locationViewMode === "list" ? "active" : ""} 
                onClick={() => setLocationViewMode("list")}
              >
                List View
              </button>
              <button 
                className={locationViewMode === "map" ? "active" : ""} 
                onClick={() => setLocationViewMode("map")}
              >
                Map View
              </button>
            </div>
          </div>

          {locationViewMode === "map" ? (
            <div className="zemuria-map-container" onClick={() => setActiveClusterId(null)}>
              <img src="/images/nations/zemuria.webp" alt="Zemuria Map" className="zemuria-map-image" />
              {(() => {
                // Group locations by unique coordinate string "x,y"
                const groups = {};
                LOCATIONS.filter(l => l.mapCoords).forEach(loc => {
                  const key = `${loc.mapCoords.x},${loc.mapCoords.y}`;
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(loc);
                });

                return Object.entries(groups).map(([coords, locs]) => {
                  const [x, y] = coords.split(",").map(Number);
                  const unlockedLocs = locs.filter(l => unlockedLocIds.has(String(l.id)));
                  const anyUnlocked = unlockedLocs.length > 0;
                  
                  if (!anyUnlocked) return null; // Hide pin entirely if nothing unlocked

                  const isMulti = unlockedLocs.length > 1;
                  const firstLoc = unlockedLocs[0];

                  return (
                    <div 
                      key={coords}
                      className="map-pin map-pin-unlocked"
                      style={{ left: `${x}%`, top: `${y}%` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isMulti) {
                          setActiveClusterId(activeClusterId === coords ? null : coords);
                        } else {
                          setSelectedItem(firstLoc);
                          setSelectedMode("locations");
                        }
                      }}
                    >
                      📍
                      {isMulti && <div className="map-pin-badge">{unlockedLocs.length}</div>}
                      
                      {!isMulti && <div className="map-pin-tooltip">{firstLoc.locationName}</div>}

                      {/* Cluster Popup */}
                      {isMulti && activeClusterId === coords && (
                        <div className="map-pin-popup" onClick={(e) => e.stopPropagation()}>
                          <div className="map-pin-popup-header">Locations at this point ({unlockedLocs.length})</div>
                          <div className="map-pin-popup-list">
                            {unlockedLocs.map(loc => {
                              return (
                                <div 
                                  key={loc.id} 
                                  className="map-pin-popup-item unlocked"
                                  onClick={() => {
                                    setSelectedItem(loc);
                                    setSelectedMode("locations");
                                    setActiveClusterId(null);
                                  }}
                                >
                                  <img 
                                    src={loc.imageFilePaths[0]} 
                                    className="map-pin-popup-img" 
                                    alt={loc.locationName} 
                                  />
                                  <span className="map-pin-popup-name">{loc.locationName}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            Array.from(new Set(LOCATIONS.map(l => l.locationNation))).sort().map(nation => {
              const locs = LOCATIONS.filter(l => l.locationNation === nation);
              const unlocked = locs.filter(l => unlockedLocIds.has(String(l.id))).length;
              const id = `loc-${nation}`;
              return (
                <CollapsibleSection
                  key={nation}
                  id={id}
                  title={nation}
                  count={unlocked}
                  total={locs.length}
                  isExpanded={expandedSections[id]}
                  onToggle={toggleSection}
                >
                  <motion.div 
                    className="collection-grid collection-grid-large"
                    initial="hidden"
                    animate="visible"
                    variants={{
                      visible: { transition: { staggerChildren: 0.05 } }
                    }}
                  >
                    {locs.map(loc => {
                      const isUnlocked = unlockedLocIds.has(String(loc.id));
                      return (
                        <motion.div
                          key={loc.id}
                          variants={{
                            hidden: { opacity: 0, scale: 0.95 },
                            visible: { opacity: 1, scale: 1 }
                          }}
                        >
                          <LocationCard
                            loc={loc}
                            isUnlocked={isUnlocked}
                            onSelect={() => (setSelectedItem(loc), setSelectedMode("locations"))}
                          />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </CollapsibleSection>
              );
            })
          )}
        </div>
      )}

      {activeSubTab === "trivia" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Trivia</span>
              <span>{tabTotals.trivia.unlocked} / {tabTotals.trivia.total} ({tabTotals.trivia.total > 0 ? Math.round((tabTotals.trivia.unlocked / tabTotals.trivia.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.trivia.unlocked} total={tabTotals.trivia.total} />
          </div>
          <div className="search-bar-container">
            <span className="search-icon-hint">🔍</span>
            <input
              type="text"
              className="search-bar-input"
              placeholder="Search trivia by keyword..."
              value={triviaSearch}
              onChange={(e) => setTriviaSearch(e.target.value)}
            />
          </div>
          <motion.div 
            className="collection-list"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.02 } }
            }}
          >
            {(() => {
              const filtered = TRIVIA.filter(t => {
                const isUnlocked = unlockedTriviaIds.has(String(t.id));
                const search = triviaSearch.toLowerCase().trim();
                const matchesSearch = search === "" ||
                  t.question.toLowerCase().includes(search) ||
                  t.answer.toLowerCase().includes(search);

                if (search === "") return true;
                return matchesSearch && isUnlocked;
              });

              return filtered.map(t => {
                const isUnlocked = unlockedTriviaIds.has(String(t.id));
                return (
                  <motion.div 
                    key={t.id} 
                    variants={{
                      hidden: { opacity: 0, x: -10 },
                      visible: { opacity: 1, x: 0 }
                    }}
                    className={`collection-list-item ${isUnlocked ? "unlocked" : ""}`}
                  >
                    <div className="collection-icon-circle">🧠</div>
                    <div className="collection-list-content">
                      <h4 className="collection-item-title">{isUnlocked ? t.question : "Locked Trivia Question"}</h4>
                      {isUnlocked && <p className="collection-item-subtitle" style={{ color: "#4caf50" }}>Answer: {t.answer}</p>}
                    </div>
                  </motion.div>
                );
              });
            })()}
          </motion.div>
        </div>
      )}

      {activeSubTab === "crafts" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Crafts</span>
              <span>{tabTotals.crafts.unlocked} / {tabTotals.crafts.total} ({tabTotals.crafts.total > 0 ? Math.round((tabTotals.crafts.unlocked / tabTotals.crafts.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.crafts.unlocked} total={tabTotals.crafts.total} />
          </div>
          {GAME_ORDER.map(gameName => {
            const gameCrafts = CRAFTS.filter(c => getGameGroupKey(c.game) === gameName);
            if (gameCrafts.length === 0) return null;
            const unlocked = gameCrafts.filter(c => unlockedCraftIds.has(String(c.id))).length;
            const id = `craft-${gameName}`;
            return (
              <CollapsibleSection
                key={gameName}
                id={id}
                title={gameName}
                count={unlocked}
                total={gameCrafts.length}
                isExpanded={expandedSections[id]}
                onToggle={toggleSection}
              >
                <motion.div 
                  className="collection-list"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.03 } }
                  }}
                >
                  {gameCrafts.map(c => {
                    const isUnlocked = unlockedCraftIds.has(String(c.id));
                    const charObj = CHARACTERS.find(char => char.name === c.character);
                    return (
                      <motion.div
                        key={c.id}
                        variants={{
                          hidden: { opacity: 0, x: -15 },
                          visible: { opacity: 1, x: 0 }
                        }}
                        className={`collection-list-item ${isUnlocked ? "unlocked" : "locked"}`}
                        onClick={() => isUnlocked && (setSelectedItem(c), setSelectedMode("crafts"))}
                      >
                        <div className="collection-icon-circle">
                          {isUnlocked && charObj?.imageUrl ? <img src={charObj.imageUrl} loading="lazy" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "⚔️"}
                        </div>
                        <div className="collection-list-content">
                          <h4 className="collection-item-title">{isUnlocked ? c.craftName : "???"}</h4>
                          <p className="collection-item-subtitle">{isUnlocked ? `${c.character} (${c.game})` : "Undiscovered Craft"}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      {activeSubTab === "factions" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>🏆 Faction War Badges</span>
              <span>{factionMonthsData ? factionMonthsData.filter(({ month, data }) => {
                const pMax = Math.max(data.bracer, data.ouroboros, data.church, 1);
                const winner = FACTIONS.find(f => data[f] === pMax);
                const userFaction = factionHistory[month];
                return userFaction && winner && userFaction === winner;
              }).length : 0} earned</span>
            </div>
            <p style={{ fontSize: "0.85rem", opacity: 0.7, margin: "5px 0 0 0" }}>
              Badges from months your faction won
            </p>
          </div>
          {factionLoading ? (
            <p style={{ textAlign: "center", opacity: 0.6 }}>Loading...</p>
          ) : factionMonthsData && factionMonthsData.length > 0 ? (
            <div className="faction-badge-grid">
              {[...factionMonthsData].reverse().map(({ month, data }) => {
                const pMax = Math.max(data.bracer, data.ouroboros, data.church, 1);
                const winner = FACTIONS.find(f => data[f] === pMax);
                const userFaction = factionHistory[month];
                const didWin = userFaction && winner && userFaction === winner;
                return (
                  <div key={month} className={`faction-badge-item ${didWin ? "unlocked" : "locked"}`}>
                    <div className="faction-badge-icon-wrapper">
                      <img
                        src={winner ? getFactionRewardBadge(month, winner) : ""}
                        alt={didWin ? "Earned Badge" : "Locked Badge"}
                        className={`faction-badge-icon ${didWin ? "earned" : ""}`}
                        onClick={() => didWin && setBadgePreview({ src: getFactionRewardBadge(month, winner), month, winner: FACTION_NAMES[winner], factionName: FACTION_NAMES[userFaction] })}
                        style={{ cursor: didWin ? "pointer" : "default" }}
                      />
                    </div>
                    <span className="faction-badge-label">{month}</span>
                    {didWin && <span className="faction-badge-earned-tag">✔</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ textAlign: "center", opacity: 0.6 }}>
              No faction data yet. Join a faction and play daily puzzles!
            </p>
          )}
        </div>
      )}

      {activeSubTab === "silhouette" && (
        <div className="collection-content">
          <div className="collection-progress-container">
            <div className="collection-progress-header">
              <span>Silhouettes</span>
              <span>{tabTotals.silhouette.unlocked} / {tabTotals.silhouette.total} ({tabTotals.silhouette.total > 0 ? Math.round((tabTotals.silhouette.unlocked / tabTotals.silhouette.total) * 100) : 0}%)</span>
            </div>
            <ProgressBar unlocked={tabTotals.silhouette.unlocked} total={tabTotals.silhouette.total} />
          </div>
          {GAME_ORDER.map(gameName => {
            const sils = SILHOUETTES.filter(s => getGameGroupKey(s.debutGame) === gameName);
            if (sils.length === 0) return null;
            const unlocked = sils.filter(s => unlockedSilIds.has(String(s.id))).length;
            const id = `sil-${gameName}`;
            return (
              <CollapsibleSection
                key={gameName}
                id={id}
                title={gameName}
                count={unlocked}
                total={sils.length}
                isExpanded={expandedSections[id]}
                onToggle={toggleSection}
              >
                <motion.div 
                  className="collection-grid"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.05 } }
                  }}
                >
                  {sils.map(sil => {
                    const isUnlocked = unlockedSilIds.has(String(sil.id));
                    return (
                      <motion.div
                        key={sil.id}
                        variants={{
                          hidden: { opacity: 0, scale: 0.85 },
                          visible: { opacity: 1, scale: 1 }
                        }}
                        className={`char-portrait-box silhouette-portrait ${isUnlocked ? "unlocked" : ""}`}
                        onClick={() => isUnlocked && (setSelectedItem(sil), setSelectedMode("silhouette"))}
                      >
                        {isUnlocked ? (
                          <img
                            src={`/images/silhouettes/${sil.id}.webp`}
                            alt={sil.character}
                            className="char-image silhouette-revealed"
                            loading="lazy"
                          />
                        ) : <div className="locked-question-mark">👤</div>}
                        {isUnlocked && <div className="char-name-label">{sil.character}</div>}
                      </motion.div>
                    );
                  })}
                </motion.div>
              </CollapsibleSection>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selectedItem && (
          <CollectionItemModal
            selectedItem={selectedItem}
            selectedMode={selectedMode}
            onClose={() => setSelectedItem(null)}
            getItemUsageStats={getItemUsageStats}
            formatProfileTrait={formatProfileTrait}
            currentTheme={currentTheme}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {badgePreview && (
          <Dialog.Root open={true} onOpenChange={(open) => !open && setBadgePreview(null)}>
            <Dialog.Portal>
              <Dialog.Overlay forceMount asChild>
                <motion.div
                  className={`modal-overlay theme-${currentTheme}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              </Dialog.Overlay>
              <div className={`modal-content-wrapper theme-${currentTheme}`}>
                <Dialog.Content forceMount asChild>
                  <motion.div
                    className="modal-content"
                    style={{ maxWidth: 400, textAlign: "center", padding: "30px" }}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  >
                    <Dialog.Title className="sr-only">Faction War Badge</Dialog.Title>
                    <Dialog.Description className="sr-only">
                      {badgePreview.month} badge earned by {badgePreview.factionName}
                    </Dialog.Description>

                    <Dialog.Close asChild>
                      <button className="modal-close">✖</button>
                    </Dialog.Close>

                    <img
                      src={badgePreview.src}
                      alt={`${badgePreview.month} Badge`}
                      style={{ width: 200, height: 200, objectFit: "contain", marginBottom: 16 }}
                    />
                    <h3 style={{ margin: "8px 0 4px" }}>{badgePreview.month}</h3>
                    <p style={{ opacity: 0.7, margin: 0 }}>
                      {badgePreview.winner}
                    </p>
                  </motion.div>
                </Dialog.Content>
              </div>
            </Dialog.Portal>
          </Dialog.Root>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Radix dialog showing detailed information and usage statistics for a discovered
 * collection entry (character, track, quote, location, trivia, craft, or silhouette).
 *
 * @param {{ selectedItem: Object, selectedMode: string, onClose: () => void, getItemUsageStats: Function, formatProfileTrait: Function, currentTheme: string }} props
 * @returns {JSX.Element}
 */
function CollectionItemModal({ selectedItem, selectedMode, onClose, getItemUsageStats, formatProfileTrait, currentTheme }) {
  const usageStats = getItemUsageStats(selectedItem.id, selectedMode);

  const renderModalContent = () => {
    switch (selectedMode) {
      case "characters":
        return (
          <>
            <h2 className="profile-char-modal-title">{selectedItem.name}</h2>
            <img src={selectedItem.imageUrl} alt={selectedItem.name} className="profile-char-modal-image" />
            <div className="profile-char-modal-details">
              <div><strong>Gender:</strong> {formatProfileTrait(selectedItem.gender)}</div>
              <div><strong>Weapon:</strong> {formatProfileTrait(selectedItem.weapon)}</div>
              <div><strong>Nationality:</strong> {formatProfileTrait(selectedItem.nationality)}</div>
              <div><strong>Affiliation:</strong> {formatProfileTrait(selectedItem.affiliation)}</div>
              <div><strong>Age:</strong> {formatProfileTrait(selectedItem.age)}</div>
              <div><strong>Debut Game:</strong> {formatProfileTrait(selectedItem.debutGame)}</div>
            </div>
          </>
        );
      case "music":
        return (
          <>
            <h2 className="profile-char-modal-title">{selectedItem.title}</h2>
            <img src={selectedItem.albumArt || "/images/nations/zemuria.webp"} alt={selectedItem.title} className="profile-char-modal-image" />
            <div className="profile-char-modal-details">
              <div><strong>Game:</strong> {selectedItem.game}</div>
              <div><strong>Type:</strong> {selectedItem.type}</div>
            </div>
          </>
        );
      case "locations":
        return (
          <>
            <h2 className="profile-char-modal-title">{selectedItem.locationName}</h2>
            <img src={selectedItem.imageFilePaths[0]} alt={selectedItem.locationName} className="profile-char-modal-image" />
            <div className="profile-char-modal-details">
              <div><strong>Nation:</strong> {selectedItem.locationNation}</div>
            </div>
          </>
        );
      case "quotes": {
        const speaker = CHARACTERS.find(c => c.name === selectedItem.character);
        const recipientChar = CHARACTERS.find(c => c.name === selectedItem.recipient);
        const recipientImg = selectedItem.recipientImage || recipientChar?.imageUrl;

        return (
          <>
            <div className="quote-modal-header">
              <div className="quote-modal-entity">
                {speaker?.imageUrl ? (
                  <img src={speaker.imageUrl} alt={selectedItem.character} className="quote-modal-img" />
                ) : (
                  <div className="quote-modal-img placeholder">👤</div>
                )}
                <span className="quote-modal-name">{selectedItem.character}</span>
              </div>
              
              <div className="quote-modal-arrow">➔</div>

              <div className="quote-modal-entity">
                {recipientImg ? (
                  <img src={recipientImg} alt={selectedItem.recipient || "General"} className="quote-modal-img" />
                ) : (
                  <div className="quote-modal-img placeholder">{selectedItem.recipient ? "👥" : "📢"}</div>
                )}
                <span className="quote-modal-name">{selectedItem.recipient || "General"}</span>
              </div>
            </div>

            <div className="profile-char-modal-quote">
              <p>"{selectedItem.quote}"</p>
            </div>
            <div className="profile-char-modal-details">
              <div><strong>Game:</strong> {selectedItem.game}</div>
            </div>
          </>
        );
      }
      case "crafts": {
        const character = CHARACTERS.find(c => c.name === selectedItem.character);
        return (
          <>
            <div className="quote-modal-header">
              <div className="quote-modal-entity">
                {character?.imageUrl ? (
                  <img src={character.imageUrl} alt={selectedItem.character} className="quote-modal-img" />
                ) : (
                  <div className="quote-modal-img placeholder">👤</div>
                )}
                <span className="quote-modal-name">{selectedItem.character}</span>
              </div>
            </div>

            <div className="profile-char-modal-quote">
              <p>"{selectedItem.description}"</p>
            </div>

            <div className="profile-char-modal-details">
              <div><strong>Craft:</strong> {selectedItem.craftName}</div>
              <div><strong>Debut Game:</strong> {selectedItem.game}</div>
            </div>
          </>
        );
      }
      case "silhouette":
        return (
          <>
            <h2 className="profile-char-modal-title">{selectedItem.character}</h2>
            <div className="silhouette-modal-image-container">
              <img src={`/images/silhouettes/${selectedItem.id}.webp`} alt={selectedItem.character} className="profile-char-modal-image silhouette-revealed" />
            </div>
            <div className="profile-char-modal-details">
              <div><strong>Debut Game:</strong> {selectedItem.debutGame}</div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div 
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content profile-char-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Discovered Entry Details</Dialog.Title>
              <Dialog.Description className="sr-only">
                Detailed information and usage statistics for {selectedItem.name || selectedItem.title || selectedItem.character || selectedItem.locationName}.
              </Dialog.Description>
    
              <Dialog.Close asChild>
                <button className="modal-close">✖</button>
              </Dialog.Close>
    
              {renderModalContent()}
    
              <div className="profile-char-modal-usage">
                <h3>Usage Statistics</h3>
                <div>Times guessed: <strong>{usageStats.timesGuessed}</strong></div>
                <div>Times correct: <strong>{usageStats.timesCorrect}</strong></div>
                <div>Success rate: <strong>{usageStats.successRate}%</strong></div>
              </div>
    
              {selectedItem.wikiUrl && (
                <a 
                  href={selectedItem.wikiUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="submit-button" 
                  style={{ textDecoration: "none", textAlign: "center", display: "block", marginTop: "15px" }}
                >
                  View on Kiseki Wiki
                </a>
              )}
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}


